import * as React from 'react';
import {CaptchaAnalyticsEventProps, CaptchaProps} from '@discordapp/captcha/CaptchaTypes';
import Captcha from '@discordapp/captcha/web/Captcha';
import {useStateFromStores} from '@discordapp/flux';

import Dispatcher from '@developers/Dispatcher';
import {isThemeDark} from '@developers/hooks/useTheme';
import CaptchaModalStore from '@developers/stores/CaptchaModalStore';
import Spinner from '@developers/uikit/Spinner';
import {track} from '@developers/utils/TrackingAnalyticsUtils';
import Modal, {ModalHeader, ModalCancel, ModalContent, ModalFooter, ModalAlert, ModalAlertTypes} from './Modal';

import {AnalyticsEvents} from '@developers/Constants';
import {Messages} from '@developers/i18n';
import type {ResponseInterceptorRetry} from '@discordapp/http-utils';
import modalStyles from './Modal.module.css';

export default function CaptchaModal() {
  const props = useStateFromStores([CaptchaModalStore], () => CaptchaModalStore.captchaModalProps);

  if (props == null) return null;
  const {cancel, retry, request} = props;

  return <NewModal onCancel={cancel} retry={retry} captchaRequest={request} />;
}

function NewModal({
  onCancel,
  retry,
  captchaRequest,
}: {
  onCancel: (e: Error) => void;
  retry: ResponseInterceptorRetry;
  captchaRequest: CaptchaProps;
}) {
  const {sitekey, captchaService, options: captchaOptions} = captchaRequest;
  const {rqtoken, serveInvisible, ...captchaProps} = captchaOptions ?? {};
  const [error, setError] = React.useState<string | null>(null);

  const renderSpinner = React.useCallback(() => {
    return <Spinner type={Spinner.Type.SPINNING_CIRCLE} />;
  }, []);

  const trackCaptchaEvent = React.useCallback((properties: CaptchaAnalyticsEventProps) => {
    track(AnalyticsEvents.CAPTCHA_EVENT, properties);
  }, []);

  function handleRequestClose() {
    onCancel(new Error('cancelled'));
    Dispatcher.dispatch({type: 'HIDE_CAPTCHA_MODAL'});
  }

  const handleCaptchaVerify = async (captchaKey: string) => {
    setError(null);
    try {
      const headers: Record<string, string> = {'X-Captcha-Key': captchaKey};
      if (rqtoken != null) {
        headers['X-Captcha-Rqtoken'] = rqtoken;
      }
      await retry(headers);
      Dispatcher.dispatch({type: 'HIDE_CAPTCHA_MODAL'});
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <Modal onRequestClose={handleRequestClose}>
      <ModalContent>
        <ModalHeader className={modalStyles.captchaModalHeader}>{Messages.GENERIC_CAPTCHA_HEADER}</ModalHeader>
        {error != null ? <ModalAlert type={ModalAlertTypes.ERROR}>{error}</ModalAlert> : null}
        <div>
          <div className={modalStyles.captchaModalContent}>
            <div>{Messages.GENERIC_CAPTCHA_DESCRIPTION}</div>
            {serveInvisible && <Spinner type={Spinner.Type.SPINNING_CIRCLE} />}
            <Captcha
              size={serveInvisible ? 'invisible' : undefined}
              theme={isThemeDark() ? 'dark' : 'light'}
              onVerify={handleCaptchaVerify}
              onClose={serveInvisible ? handleRequestClose : undefined}
              renderSpinner={renderSpinner}
              track={trackCaptchaEvent}
              captchaService={captchaService}
              sitekey={sitekey}
              {...captchaProps}
            />
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <ModalCancel onClick={handleRequestClose}>{Messages.ACTIONS_CANCEL}</ModalCancel>
      </ModalFooter>
    </Modal>
  );
}
